import { z } from "zod";

import {
  BinaryColumnPredicateOpLiteral,
  CompoundColumnPredicateOp,
  CompoundColumnPredicateOpLiteral,
  LumpColumnPredicateOpLiteral,
  UnaryColumnPredicateOpLiteral,
} from "../display-table/index.js";
import { FilledDynamicValueTableColumnTypeLiteral } from "../DynamicValue.js";
import { FilterCellColumnId } from "../idTypeBrands.js";
import { getNormalEnum } from "../pseudoEnums.js";

export const FilterCellSpecificUnaryColumnPredicateOpLiteral = z.enum([
  "IS_EMPTY",
  "NOT_EMPTY",
]);

export const FilterCellUnaryColumnPredicateOpLiteral = z.union([
  FilterCellSpecificUnaryColumnPredicateOpLiteral,
  UnaryColumnPredicateOpLiteral,
]);
export type FilterCellUnaryColumnPredicateOp = z.infer<
  typeof FilterCellUnaryColumnPredicateOpLiteral
>;
export const FilterCellUnaryColumnPredicateOp = {
  ...getNormalEnum(FilterCellSpecificUnaryColumnPredicateOpLiteral),
  ...getNormalEnum(UnaryColumnPredicateOpLiteral),
};
export const FilterCellUnaryColumnPredicate = z.object({
  op: FilterCellUnaryColumnPredicateOpLiteral,
});
export type FilterCellUnaryColumnPredicate = z.infer<
  typeof FilterCellUnaryColumnPredicate
>;

export const FilterCellBinaryColumnPredicateOpLiteral =
  BinaryColumnPredicateOpLiteral;

export type FilterCellBinaryColumnPredicateOp = z.infer<
  typeof FilterCellBinaryColumnPredicateOpLiteral
>;

export const FilterCellBinaryColumnPredicateOp = {
  ...getNormalEnum(BinaryColumnPredicateOpLiteral),
};

export const FilterCellBinaryColumnPredicate = z.object({
  op: FilterCellBinaryColumnPredicateOpLiteral,
  arg: z.string(),
  // `allowInterpolation` is omitted on the persisted db representation and added when
  // passed them into the kernel. In practice this is used to to determine whether to
  // support jinja templating in filters.
  allowInterpolation: z.boolean().optional(),
});
export type FilterCellBinaryColumnPredicate = z.infer<
  typeof FilterCellBinaryColumnPredicate
>;

export const FilterCellListBinaryColumnPredicateOpLiteral = z.enum([
  "IS_ONE_OF",
  "NOT_ONE_OF",
  "DATE_BETWEEN",
]);

export type FilterCellListBinaryColumnPredicateOp = z.infer<
  typeof FilterCellListBinaryColumnPredicateOpLiteral
>;

export const FilterCellListBinaryColumnPredicateOp = getNormalEnum(
  FilterCellListBinaryColumnPredicateOpLiteral,
);

export const FilterCellListBinaryColumnPredicate = z.object({
  op: FilterCellListBinaryColumnPredicateOpLiteral,
  arg: z.array(z.string()),
  // `allowInterpolation` is omitted on the persisted db representation and added when
  // passed them into the kernel. In practice this is used to to determine whether to
  // support jinja templating in filters.
  allowInterpolation: z.boolean().optional(),
});

export type FilterCellListBinaryColumnPredicate = z.infer<
  typeof FilterCellListBinaryColumnPredicate
>;

export const FilterCellLumpColumnPredicateOpLiteral =
  LumpColumnPredicateOpLiteral;

export type FilterCellLumpColumnPredicateOp = z.infer<
  typeof FilterCellLumpColumnPredicateOpLiteral
>;

export const FilterCellLumpColumnPredicateOp = {
  ...getNormalEnum(LumpColumnPredicateOpLiteral),
};

export const FilterCellLumpColumnPredicate = z.object({
  op: FilterCellLumpColumnPredicateOpLiteral,
  arg: z.string(),
});

export type FilterCellLumpColumnPredicate = z.infer<
  typeof FilterCellLumpColumnPredicate
>;

export type FilterCellColumnPredicate =
  | FilterCellBinaryColumnPredicate
  | FilterCellUnaryColumnPredicate
  | FilterCellListBinaryColumnPredicate
  | FilterCellLumpColumnPredicate;

export type FilterCellColumnPredicateOp = FilterCellColumnPredicate["op"];

export const ColumnOperation = z.union([
  FilterCellUnaryColumnPredicate,
  FilterCellBinaryColumnPredicate,
  FilterCellListBinaryColumnPredicate,
  FilterCellLumpColumnPredicate,
]);

export type ColumnOperation = z.infer<typeof ColumnOperation>;

export const Filter = z.object({
  column: FilterCellColumnId,
  operation: ColumnOperation,
  columnType: FilledDynamicValueTableColumnTypeLiteral,
  matchCase: z.boolean().optional(),
});
export type Filter = z.infer<typeof Filter>;

export type FilterGroup = {
  operation: CompoundColumnPredicateOp;
  filters: (Filter | FilterGroup)[];
};

export const FilterGroup: z.ZodType<FilterGroup, z.ZodTypeDef, unknown> =
  z.lazy(() =>
    z.object({
      operation: CompoundColumnPredicateOpLiteral,
      filters: z.array(z.union([Filter, FilterGroup])),
    }),
  );

export const FilterTypeLiteral = z.enum(["KEEP", "REMOVE"]);
export type FilterType = z.infer<typeof FilterTypeLiteral>;
export const FilterType = getNormalEnum(FilterTypeLiteral);

export const FilterCellAppMode = {
  FULL_ACCESS: "FULL_ACCESS",
  VALUES_ONLY: "VALUES_ONLY",
} as const;
export type FilterCellAppMode =
  (typeof FilterCellAppMode)[keyof typeof FilterCellAppMode];

export const DistinctColumnValues = z.record(
  z.string(),
  z.object({
    value: z.array(z.union([z.string(), z.null(), z.number(), z.boolean()])),
    truncated: z.boolean(),
  }),
);

export type DistinctColumnValues = z.infer<typeof DistinctColumnValues>;
